import { useCallback, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { AppLayout } from '@/components/layout/AppLayout'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useHomeStore } from '@/features/home/store'
import { ClientInfoCard } from '@/features/home/components/ClientInfoCard'
import { SessionsTabBar } from '@/features/home/components/SessionsTabBar'
import { DocumentUploadCard } from '@/features/home/components/DocumentUploadCard'
import { ManualVinCard } from '@/features/home/components/ManualVinCard'
import { VinProgressBar } from '@/features/home/components/VinProgressBar'
import { VehicleList } from '@/features/home/components/VehicleList'
import { BulkImportModal } from '@/features/home/components/BulkImportModal'
import { DownloadButton } from '@/features/home/components/DownloadButton'
import { useVinQueue } from '@/features/home/hooks/useVinQueue'

export function HomePage() {
  const vehicles = useHomeStore((s) => s.vehicles)
  const resetAll = useHomeStore((s) => s.resetAll)
  const currentSessionId = useHomeStore((s) => s.currentSessionId)
  const resetVersion = useHomeStore((s) => s.resetVersion)
  const { state: queue, enqueue, cancel } = useVinQueue()
  const [bulk, setBulk] = useState<{ file: File; parsed: import('@/features/home/api').ParseXlsxStandard } | null>(null)
  const [confirmClear, setConfirmClear] = useState(false)

  const enqueueByCarIds = useCallback(
    (ids: number[]) => {
      const all = useHomeStore.getState().vehicles
      const jobs = ids
        .map((id) => all.find((v) => v.id === id))
        .filter((v): v is NonNullable<typeof v> => !!v && !!v.vin && v.vin.length === 17 && !v.car_gov_data?.Data)
        .map((v) => ({ carId: v.id, vin: v.vin }))
      if (jobs.length > 0) enqueue(jobs)
    },
    [enqueue],
  )

  return (
    <AppLayout>
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        <SessionsTabBar />
        <ClientInfoCard />

        <section className="overflow-hidden rounded-lg bg-white shadow-sm">
          <header className="bg-gradient-to-r from-purple-500 to-orange-400 px-6 py-4">
            <h2 className="text-base font-semibold text-white">Nahrání a extrakce dokumentů</h2>
          </header>
          <div className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-2">
            <DocumentUploadCard
              key={`${currentSessionId}-${resetVersion}`}
              onExtracted={enqueueByCarIds}
              onBulkXlsx={(file, parsed) => setBulk({ file, parsed })}
            />
            <div className="space-y-6">
              <ManualVinCard onAdded={enqueueByCarIds} />
              <VinProgressBar
                active={queue.active}
                completed={queue.completed}
                total={queue.total}
                status={queue.status}
                onCancel={cancel}
              />
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-lg bg-white shadow-sm">
          <header className="bg-gradient-to-r from-purple-500 to-orange-400 px-6 py-4">
            <h2 className="text-base font-semibold text-white">Výsledky vozidel ({vehicles.length})</h2>
          </header>
          <div className="p-6">
            <VehicleList onReenqueue={(id) => enqueueByCarIds([id])} />
          </div>
        </section>

        <div className="flex flex-wrap items-center justify-center gap-4">
          <DownloadButton />
          <Button variant="outline" onClick={() => setConfirmClear(true)}>
            <Trash2 className="mr-2 h-4 w-4 text-red-500" />
            Vyčistit formulář
          </Button>
        </div>
      </div>

      <BulkImportModal
        file={bulk?.file ?? null}
        parsed={bulk?.parsed ?? null}
        onClose={() => setBulk(null)}
        onImported={(ids) => {
          enqueueByCarIds(ids)
        }}
      />

      <Dialog open={confirmClear} onOpenChange={(o) => !o && setConfirmClear(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Vyčistit formulář</DialogTitle>
            <DialogDescription>
              Opravdu smazat všechna data? Pojistník, vozidla i pojistné krytí se vrátí do výchozího stavu.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmClear(false)}>
              Zrušit
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                resetAll()
                setConfirmClear(false)
              }}
            >
              Vyčistit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}
